import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  AlertPreferenceMap,
  AlertPreferenceProfile,
  MemberRole,
  MemberWithUser,
  Organisation,
  OrganisationInviteWithContext,
  SupportedLocale,
} from '@accestra/types';
import { useAuth } from '../hooks/useAuth';
import { api, ApiError, friendlyError } from '../lib/api-client';
import { getPlanLabel } from '../lib/plans';
import i18n from '../i18n/i18n';
import { formatDateTime as formatLocaleDateTime } from '../i18n/formatters';
import Container from '../components/ui/Container';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import AlertBanner from '../components/ui/AlertBanner';
import SectionTabs from '../components/ui/SectionTabs';

interface OrganisationResponse {
  organisation: Organisation;
}

interface MembersResponse {
  members: MemberWithUser[];
}

interface InvitesResponse {
  invites: OrganisationInviteWithContext[];
}

interface AlertPreferencesResponse {
  preferences: AlertPreferenceProfile;
}

interface InviteResponse {
  invite: OrganisationInviteWithContext;
  inviteToken: string;
}

interface OrgUsageResponse {
  plan: string;
  limits: {
    maxSites: number;
    maxScansPerMonth: number;
    maxPagesPerScan: number;
    maxTeamMembers: number;
    scheduledScansEnabled: boolean;
    alertsEnabled: boolean;
    authenticatedScanningEnabled: boolean;
    complianceExportEnabled: boolean;
  };
  usage: {
    sitesCount: number;
    scansThisMonth: number;
    teamMembersCount: number;
  };
}

interface RetentionSummaryResponse {
  summary: {
    activeCount: number;
    archivedCount: number;
    eligibleForCleanupCount: number;
    authDiagnosticCleanupCandidates: number;
  };
}

interface GitHubIntegrationSettings {
  provider: 'github';
  repoOwner: string | null;
  repoName: string | null;
  isEnabled: boolean;
  tokenConfigured: boolean;
  lastTestedAt: string | Date | null;
  lastTestStatus: 'passed' | 'failed' | null;
  lastTestError: string | null;
}

interface GitHubIntegrationResponse {
  integration: GitHubIntegrationSettings;
}

interface GitHubIntegrationTestResponse {
  ok: boolean;
  message: string;
  integration: GitHubIntegrationSettings;
}

type WorkspaceSection =
  | 'overview'
  | 'team'
  | 'invites'
  | 'notifications'
  | 'governance'
  | 'usage';

const C = {
  page: 'var(--acc-color-page)',
  text: 'var(--acc-color-text)',
  muted: 'var(--acc-color-text-muted)',
  border: 'var(--acc-color-border)',
  card: 'var(--card-bg)',
  surfaceSecondary: 'var(--surface-secondary)',
  inputBg: 'var(--input-bg)',
  buttonSecondary: 'var(--button-secondary-bg)',
  link: 'var(--acc-color-primary)',
};

const fieldStyle = (color = C.text) => ({
  width: '100%',
  minHeight: 42,
  borderRadius: 10,
  border: `1px solid ${C.border}`,
  padding: '10px 12px',
  fontSize: 14,
  background: C.inputBg,
  color,
});

const staticFieldStyle = {
  padding: '11px 12px',
  borderRadius: 10,
  border: `1px solid ${C.border}`,
  background: C.surfaceSecondary,
  fontSize: 14,
  color: C.text,
} as const;

const panelStyle = {
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  padding: '14px 16px',
  background: C.card,
} as const;

function toneColor(tone: 'success' | 'danger') {
  return tone === 'danger' ? 'var(--acc-color-danger)' : 'var(--acc-color-success)';
}

const DEFAULT_GITHUB_INTEGRATION: GitHubIntegrationSettings = {
  provider: 'github',
  repoOwner: null,
  repoName: null,
  isEnabled: false,
  tokenConfigured: false,
  lastTestedAt: null,
  lastTestStatus: null,
  lastTestError: null,
};

function preferenceLabel(key: keyof AlertPreferenceMap): string {
  switch (key) {
    case 'scanCompleted': return i18n.t('workspace:preferences.labels.scanCompleted');
    case 'scanFailed': return i18n.t('workspace:preferences.labels.scanFailed');
    case 'authOperationalFailure': return i18n.t('workspace:preferences.labels.authOperationalFailure');
    case 'issueRegression': return i18n.t('workspace:preferences.labels.issueRegression');
    case 'issueReappeared': return i18n.t('workspace:preferences.labels.issueReappeared');
    default: return key;
  }
}

function roleBadgeVariant(role: MemberRole): 'info' | 'warning' | 'neutral' {
  switch (role) {
    case 'owner': return 'info';
    case 'admin': return 'warning';
    case 'member':
    default:
      return 'neutral';
  }
}

function roleLabel(role: MemberRole): string {
  switch (role) {
    case 'member': return i18n.t('common:roles.member');
    case 'admin': return i18n.t('common:roles.admin');
    case 'owner': return i18n.t('common:roles.owner');
    default: return role;
  }
}

function statusBadgeVariant(status: 'active' | 'inactive'): 'success' | 'neutral' {
  return status === 'active' ? 'success' : 'neutral';
}

function inviteStatusVariant(status: OrganisationInviteWithContext['status']): 'info' | 'success' | 'warning' | 'neutral' {
  switch (status) {
    case 'accepted': return 'success';
    case 'expired': return 'warning';
    case 'revoked': return 'neutral';
    case 'pending':
    default:
      return 'info';
  }
}

function inviteStatusLabel(status: OrganisationInviteWithContext['status']): string {
  switch (status) {
    case 'accepted': return i18n.t('common:inviteStatuses.accepted');
    case 'expired': return i18n.t('common:inviteStatuses.expired');
    case 'revoked': return i18n.t('common:inviteStatuses.revoked');
    case 'pending':
    default:
      return i18n.t('common:inviteStatuses.pending');
  }
}

function formatDateTime(value: string | Date | null | undefined): string {
  return formatLocaleDateTime(value) || i18n.t('common:labels.notAvailable');
}

function formatRetentionDays(days: number | null | undefined): string {
  if (days == null) return i18n.t('workspace:retention.manualGovernance');
  return i18n.t('workspace:retention.days', { count: days });
}

export default function WorkspaceSettingsPage() {
  const { t } = useTranslation(['workspace', 'common']);
  const { org, role, user, syncLocalePreferences } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManageWorkspace = role === 'owner' || role === 'admin';
  const isOwner = role === 'owner';
  type FeedbackTone = 'success' | 'danger';

  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [members, setMembers] = useState<MemberWithUser[]>([]);
  const [invites, setInvites] = useState<OrganisationInviteWithContext[]>([]);
  const [preferences, setPreferences] = useState<AlertPreferenceProfile | null>(null);
  const [preferenceDraft, setPreferenceDraft] = useState<AlertPreferenceMap | null>(null);
  const [usageData, setUsageData] = useState<OrgUsageResponse | null>(null);
  const [retentionSummary, setRetentionSummary] = useState<RetentionSummaryResponse['summary'] | null>(null);

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [defaultLocale, setDefaultLocale] = useState<SupportedLocale>('en');
  const [workspaceFeedback, setWorkspaceFeedback] = useState<{ message: string; tone: FeedbackTone } | null>(null);
  const [savingWorkspace, setSavingWorkspace] = useState(false);
  const [githubRepoOwner, setGitHubRepoOwner] = useState('');
  const [githubRepoName, setGitHubRepoName] = useState('');
  const [githubAccessToken, setGitHubAccessToken] = useState('');
  const [githubEnabled, setGitHubEnabled] = useState(false);
  const [githubTokenConfigured, setGitHubTokenConfigured] = useState(false);
  const [githubLastTestedAt, setGitHubLastTestedAt] = useState<string | Date | null>(null);
  const [githubLastTestStatus, setGitHubLastTestStatus] = useState<'passed' | 'failed' | null>(null);
  const [githubLastTestError, setGitHubLastTestError] = useState<string | null>(null);
  const [githubIntegrationFeedback, setGitHubIntegrationFeedback] = useState<{ message: string; tone: FeedbackTone } | null>(null);
  const [savingGitHubIntegration, setSavingGitHubIntegration] = useState(false);
  const [testingGitHubIntegration, setTestingGitHubIntegration] = useState(false);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteFeedback, setInviteFeedback] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [latestInviteLink, setLatestInviteLink] = useState<{ inviteId: string; link: string } | null>(null);
  const [inviting, setInviting] = useState(false);
  const [copyingInviteId, setCopyingInviteId] = useState<string | null>(null);

  const [savingPreference, setSavingPreference] = useState(false);
  const [preferenceFeedback, setPreferenceFeedback] = useState<{ message: string; tone: FeedbackTone } | null>(null);

  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const [memberFeedback, setMemberFeedback] = useState<string | null>(null);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [revokingInviteId, setRevokingInviteId] = useState<string | null>(null);
  const requestedSection = searchParams.get('section');
  const activeSection: WorkspaceSection =
    requestedSection === 'team' ||
    requestedSection === 'invites' ||
    requestedSection === 'notifications' ||
    requestedSection === 'governance' ||
    requestedSection === 'usage'
      ? requestedSection
      : 'overview';

  const applyGitHubIntegrationState = useCallback((integration: GitHubIntegrationSettings | null | undefined) => {
    const next = integration ?? DEFAULT_GITHUB_INTEGRATION;
    setGitHubRepoOwner(next.repoOwner ?? '');
    setGitHubRepoName(next.repoName ?? '');
    setGitHubEnabled(next.isEnabled);
    setGitHubTokenConfigured(next.tokenConfigured);
    setGitHubLastTestedAt(next.lastTestedAt ?? null);
    setGitHubLastTestStatus(next.lastTestStatus ?? null);
    setGitHubLastTestError(next.lastTestError ?? null);
    setGitHubAccessToken('');
  }, []);

  const loadData = useCallback(async () => {
    if (!org?.slug) return;

    setLoading(true);
    setPageError(null);

    try {
      const requests: Array<Promise<unknown>> = [
        api.get<OrganisationResponse>(`/orgs/${org.slug}`),
        api.get<MembersResponse>(`/orgs/${org.slug}/members`),
        api.get<AlertPreferencesResponse>('/alerts/preferences'),
        api.get<OrgUsageResponse>(`/orgs/${org.slug}/usage`),
        api.get<RetentionSummaryResponse>(`/orgs/${org.slug}/retention-summary`),
      ];

      if (canManageWorkspace) {
        requests.push(api.get<InvitesResponse>(`/orgs/${org.slug}/invites`));
        requests.push(api.get<GitHubIntegrationResponse>('/settings/integrations/github'));
      }

      const [
        orgData,
        membersData,
        preferenceData,
        usageResponse,
        retentionResponse,
        invitesResponse,
        githubIntegrationResponse,
      ] = await Promise.all(requests) as [
        OrganisationResponse,
        MembersResponse,
        AlertPreferencesResponse,
        OrgUsageResponse,
        RetentionSummaryResponse,
        InvitesResponse | undefined,
        GitHubIntegrationResponse | undefined,
      ];

      setOrganisation(orgData.organisation);
      setMembers(membersData.members ?? []);
      setInvites(invitesResponse?.invites ?? []);
      setPreferences(preferenceData.preferences ?? null);
      setPreferenceDraft(preferenceData.preferences?.organisationDefaults ?? null);
      setUsageData(usageResponse ?? null);
      setRetentionSummary(retentionResponse?.summary ?? null);
      setDisplayName(orgData.organisation.name);
      setNotificationEmail(orgData.organisation.settings.notificationEmail ?? '');
      setDefaultLocale(orgData.organisation.settings.defaultLocale ?? 'en');
      if (canManageWorkspace) {
        applyGitHubIntegrationState(githubIntegrationResponse?.integration);
      } else {
        applyGitHubIntegrationState(DEFAULT_GITHUB_INTEGRATION);
      }
    } catch (err) {
      setPageError(friendlyError(err, t('page.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [applyGitHubIntegrationState, canManageWorkspace, org?.slug, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const workspaceMembersSummary = useMemo(() => {
    const owners = members.filter((member) => member.role === 'owner').length;
    const admins = members.filter((member) => member.role === 'admin').length;
    const viewers = members.filter((member) => member.role === 'member').length;
    const active = members.filter((member) => member.status === 'active').length;
    const inactive = members.filter((member) => member.status === 'inactive').length;
    const users = members.length;
    return { owners, admins, viewers, active, inactive, users };
  }, [members]);

  const pendingInvites = useMemo(
    () => invites.filter((invite) => invite.status === 'pending'),
    [invites],
  );
  const teamSeatLimit = usageData?.limits.maxTeamMembers ?? null;
  const teamSeatLimitReached = teamSeatLimit !== null && (usageData?.usage.teamMembersCount ?? members.length) >= teamSeatLimit;
  const workspaceSections = [
    { id: 'overview', label: t('sections.overview') },
    { id: 'team', label: t('sections.team'), badge: workspaceMembersSummary.users > 0 ? workspaceMembersSummary.users : undefined },
    { id: 'invites', label: t('sections.invites'), badge: pendingInvites.length > 0 ? pendingInvites.length : undefined },
    { id: 'notifications', label: t('sections.notifications') },
    { id: 'governance', label: t('sections.governance') },
    { id: 'usage', label: t('sections.usage') },
  ] satisfies Array<{ id: WorkspaceSection; label: string; badge?: string | number }>;

  const handleWorkspaceSectionChange = (section: WorkspaceSection) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (section === 'overview') {
        next.delete('section');
      } else {
        next.set('section', section);
      }
      return next;
    });
  };

  const canSaveGitHubIntegration =
    canManageWorkspace
    && githubRepoOwner.trim().length > 0
    && githubRepoName.trim().length > 0
    && (githubTokenConfigured || githubAccessToken.trim().length > 0);
  const canTestGitHubIntegration =
    canManageWorkspace
    && githubRepoOwner.trim().length > 0
    && githubRepoName.trim().length > 0
    && githubTokenConfigured;

  const commercialSummary = useMemo(() => {
    if (!usageData) return null;

    const sitePct = usageData.limits.maxSites > 0
      ? usageData.usage.sitesCount / usageData.limits.maxSites
      : 0;
    const seatPct = usageData.limits.maxTeamMembers > 0
      ? usageData.usage.teamMembersCount / usageData.limits.maxTeamMembers
      : 0;
    const scanPct = usageData.limits.maxScansPerMonth > 0
      ? usageData.usage.scansThisMonth / usageData.limits.maxScansPerMonth
      : 0;

    const pressureKey =
      sitePct >= 1
        ? 'siteLimitReached'
        : seatPct >= 1
          ? 'seatLimitReached'
          : scanPct >= 1
            ? 'scanLimitReached'
            : sitePct >= 0.8 || seatPct >= 0.8 || scanPct >= 0.8
              ? 'nearLimit'
              : 'withinLimit';

    const postureKey = usageData.plan === 'starter'
      ? 'starter'
      : usageData.plan === 'growth' || usageData.plan === 'professional'
        ? 'growth'
        : usageData.plan === 'enterprise'
          ? 'enterprise'
          : 'free';

    return {
      currentPlanLabel: getPlanLabel(usageData.plan),
      pressureKey,
      postureKey,
    };
  }, [usageData]);

  const handleSaveWorkspace = useCallback(async () => {
    if (!org?.slug) return;

    setSavingWorkspace(true);
    setWorkspaceFeedback(null);

    try {
      const response = await api.patch<OrganisationResponse>(`/orgs/${org.slug}`, {
        name: displayName.trim(),
        settings: {
          notificationEmail: notificationEmail.trim() || null,
          defaultLocale,
        },
      });

      setOrganisation(response.organisation);
      setDisplayName(response.organisation.name);
      setNotificationEmail(response.organisation.settings.notificationEmail ?? '');
      setDefaultLocale(response.organisation.settings.defaultLocale ?? 'en');
      syncLocalePreferences({
        defaultLocale: response.organisation.settings.defaultLocale ?? 'en',
      });
      setWorkspaceFeedback({ message: t('profile.saved'), tone: 'success' });
    } catch (err) {
      setWorkspaceFeedback({ message: friendlyError(err, t('profile.saveFailed')), tone: 'danger' });
    } finally {
      setSavingWorkspace(false);
    }
  }, [defaultLocale, displayName, notificationEmail, org?.slug, syncLocalePreferences, t]);

  const handleSaveGitHubIntegration = useCallback(async () => {
    setSavingGitHubIntegration(true);
    setGitHubIntegrationFeedback(null);

    try {
      const response = await api.put<GitHubIntegrationResponse>('/settings/integrations/github', {
        repoOwner: githubRepoOwner.trim(),
        repoName: githubRepoName.trim(),
        accessToken: githubAccessToken.trim() || undefined,
        isEnabled: githubEnabled,
      });

      applyGitHubIntegrationState(response.integration);
      setGitHubIntegrationFeedback({ message: t('githubIntegration.saved'), tone: 'success' });
    } catch (err) {
      setGitHubIntegrationFeedback({
        message: friendlyError(err, t('githubIntegration.saveFailed')),
        tone: 'danger',
      });
    } finally {
      setSavingGitHubIntegration(false);
    }
  }, [
    applyGitHubIntegrationState,
    githubAccessToken,
    githubEnabled,
    githubRepoName,
    githubRepoOwner,
    t,
  ]);

  const handleTestGitHubIntegration = useCallback(async () => {
    setTestingGitHubIntegration(true);
    setGitHubIntegrationFeedback(null);

    try {
      const response = await api.post<GitHubIntegrationTestResponse>('/settings/integrations/github/test');
      applyGitHubIntegrationState(response.integration);
      setGitHubIntegrationFeedback({
        message: response.ok
          ? t('githubIntegration.testPassed')
          : t('githubIntegration.testFailedWithMessage', { message: response.message }),
        tone: response.ok ? 'success' : 'danger',
      });
    } catch (err) {
      setGitHubIntegrationFeedback({
        message: friendlyError(err, t('githubIntegration.testFailed')),
        tone: 'danger',
      });
    } finally {
      setTestingGitHubIntegration(false);
    }
  }, [applyGitHubIntegrationState, t]);

  const handleInvite = useCallback(async () => {
    if (!org?.slug) return;

    setInviting(true);
    setInviteFeedback(null);
    setInviteError(null);

    try {
      if (teamSeatLimitReached) {
        setInviteError(t('invites.limitReached', { count: teamSeatLimit ?? 0 }));
        return;
      }

      const response = await api.post<InviteResponse>(`/orgs/${org.slug}/invites`, {
        email: inviteEmail.trim(),
        role: inviteRole,
        message: inviteMessage.trim() || null,
      });

      const inviteLink = `${window.location.origin}/accept-invite/${response.inviteToken}`;
      setInviteEmail('');
      setInviteRole('member');
      setInviteMessage('');
      setLatestInviteLink({ inviteId: response.invite.id, link: inviteLink });
      setInviteFeedback(t('invites.created', { email: response.invite.email }));
      await loadData();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('invites.createFailed');
      setInviteError(message);
    } finally {
      setInviting(false);
    }
  }, [inviteEmail, inviteMessage, inviteRole, loadData, org?.slug, t, teamSeatLimit, teamSeatLimitReached]);

  const handleCopyInviteLink = useCallback(async (inviteId: string, link: string) => {
    setCopyingInviteId(inviteId);
    setInviteFeedback(null);
    setInviteError(null);
    let resolvedLink = link;
    try {
      if (!resolvedLink && org?.slug) {
        const response = await api.post<InviteResponse>(`/orgs/${org.slug}/invites/${inviteId}/link`);
        resolvedLink = `${window.location.origin}/accept-invite/${response.inviteToken}`;
        setLatestInviteLink({ inviteId: response.invite.id, link: resolvedLink });
      }

      await navigator.clipboard.writeText(resolvedLink);
      setInviteFeedback(t('invites.copied'));
    } catch {
      if (resolvedLink) {
        setLatestInviteLink({ inviteId, link: resolvedLink });
        setInviteFeedback(t('invites.copyFallback', { link: resolvedLink }));
      } else {
        setInviteError(t('invites.copyFailed'));
      }
    } finally {
      setCopyingInviteId(null);
    }
  }, [org?.slug, t]);

  const handleMemberRoleChange = useCallback(async (member: MemberWithUser, nextRole: 'admin' | 'member') => {
    if (!org?.slug) return;

    setUpdatingMemberId(member.id);
    setMemberFeedback(null);
    setMemberError(null);

    try {
      await api.patch(`/orgs/${org.slug}/members/${member.id}`, { role: nextRole });
      setMemberFeedback(t('members.roleUpdated', { email: member.userEmail, role: roleLabel(nextRole).toLowerCase() }));
      await loadData();
    } catch (err) {
      setMemberError(friendlyError(err, t('members.roleFailed')));
    } finally {
      setUpdatingMemberId(null);
    }
  }, [loadData, org?.slug, t]);

  const handleMemberStatusChange = useCallback(async (member: MemberWithUser, nextStatus: 'active' | 'inactive') => {
    if (!org?.slug) return;

    setUpdatingMemberId(member.id);
    setMemberFeedback(null);
    setMemberError(null);

    try {
      await api.patch(`/orgs/${org.slug}/members/${member.id}/status`, { status: nextStatus });
      setMemberFeedback(
        nextStatus === 'inactive'
          ? t('members.deactivated', { email: member.userEmail })
          : t('members.reactivated', { email: member.userEmail }),
      );
      await loadData();
    } catch (err) {
      setMemberError(friendlyError(err, t('members.statusFailed')));
    } finally {
      setUpdatingMemberId(null);
    }
  }, [loadData, org?.slug, t]);

  const handleRevokeInvite = useCallback(async (inviteId: string) => {
    if (!org?.slug) return;
    setRevokingInviteId(inviteId);
    setInviteFeedback(null);
    setInviteError(null);
    try {
      await api.post(`/orgs/${org.slug}/invites/${inviteId}/revoke`);
      if (latestInviteLink?.inviteId === inviteId) {
        setLatestInviteLink(null);
      }
      setInviteFeedback(t('invites.revoked'));
      await loadData();
    } catch (err) {
      setInviteError(friendlyError(err, t('invites.revokeFailed')));
    } finally {
      setRevokingInviteId(null);
    }
  }, [latestInviteLink, loadData, org?.slug, t]);

  const handlePreferenceChange = useCallback((key: keyof AlertPreferenceMap, value: boolean) => {
    setPreferenceDraft((prev) => ({
      scanCompleted: prev?.scanCompleted ?? preferences?.organisationDefaults.scanCompleted ?? true,
      scanFailed: prev?.scanFailed ?? preferences?.organisationDefaults.scanFailed ?? true,
      authOperationalFailure: prev?.authOperationalFailure ?? preferences?.organisationDefaults.authOperationalFailure ?? true,
      issueRegression: prev?.issueRegression ?? preferences?.organisationDefaults.issueRegression ?? true,
      issueReappeared: prev?.issueReappeared ?? preferences?.organisationDefaults.issueReappeared ?? true,
      [key]: value,
    }));
  }, [preferences]);

  const handleSavePreferences = useCallback(async () => {
    if (!preferenceDraft) return;

    setSavingPreference(true);
    setPreferenceFeedback(null);
    try {
      const data = await api.patch<AlertPreferencesResponse>('/alerts/preferences', preferenceDraft);
      setPreferences(data.preferences ?? null);
      setPreferenceDraft(data.preferences?.organisationDefaults ?? null);
      setPreferenceFeedback({ message: t('preferences.saved'), tone: 'success' });
    } catch (err) {
      setPreferenceFeedback({ message: friendlyError(err, t('preferences.saveFailed')), tone: 'danger' });
    } finally {
      setSavingPreference(false);
    }
  }, [preferenceDraft, t]);

  return (
    <Container maxWidth={1320}>
        <div style={{ marginBottom: 24 }}>
          <PageHeader
            eyebrow={t('header.eyebrow')}
            title={t('header.title')}
            subtitle={organisation?.name ?? org?.name ?? t('header.subtitleFallback')}
            description={t('header.description')}
            meta={
              <>
                <Badge variant="info">{usageData?.plan ? usageData.plan.charAt(0).toUpperCase() + usageData.plan.slice(1) : t('common:labels.workspace')}</Badge>
                <Badge variant="info">{t('members.active', { count: workspaceMembersSummary.active })}</Badge>
                <Badge variant="warning">{t('posture.pendingInvites', { count: pendingInvites.length })}</Badge>
                {organisation?.settings.notificationEmail && (
                  <Badge variant="neutral">{organisation.settings.notificationEmail}</Badge>
                )}
              </>
            }
            action={
              <Link
                to="/dashboard"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 40,
                  padding: '9px 16px',
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  background: C.buttonSecondary,
                  color: C.text,
                  textDecoration: 'none',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {t('common:navigation.backToDashboard')}
              </Link>
            }
          />
        </div>

        {pageError && (
          <AlertBanner variant="danger" style={{ marginBottom: 20 }}>
            {pageError}
          </AlertBanner>
        )}

        <div style={{ marginBottom: 20 }}>
          <SectionTabs
            items={workspaceSections}
            activeId={activeSection}
            onChange={(sectionId) => handleWorkspaceSectionChange(sectionId as WorkspaceSection)}
            sticky
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              activeSection === 'overview' || activeSection === 'invites'
                ? 'minmax(0, 1.35fr) minmax(320px, 0.95fr)'
                : 'minmax(0, 1fr)',
            gap: 22,
            alignItems: 'start',
          }}
        >
          <div style={{ display: 'grid', gap: 20 }}>
            {activeSection === 'overview' && (
            <Card>
              <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                {t('profile.title')}
              </div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 18 }}>
                {t('profile.description')}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('profile.displayName')}
                  </span>
                  <input
                    type="text"
                    value={displayName}
                    disabled={!canManageWorkspace || loading}
                    onChange={(event) => {
                      setDisplayName(event.target.value);
                      setWorkspaceFeedback(null);
                    }}
                    style={fieldStyle()}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('profile.notificationEmail')}
                  </span>
                  <input
                    type="email"
                    value={notificationEmail}
                    disabled={!canManageWorkspace || loading}
                    onChange={(event) => {
                      setNotificationEmail(event.target.value);
                      setWorkspaceFeedback(null);
                    }}
                    placeholder={user?.email ?? t('profile.placeholderEmail')}
                    style={fieldStyle()}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('profile.defaultLocale')}
                  </span>
                  <select
                    value={defaultLocale}
                    disabled={!canManageWorkspace || loading}
                    onChange={(event) => {
                      setDefaultLocale(event.target.value as SupportedLocale);
                      setWorkspaceFeedback(null);
                    }}
                    style={fieldStyle()}
                  >
                    <option value="en">{t('common:locales.en')}</option>
                    <option value="de">{t('common:locales.de')}</option>
                  </select>
                </label>
              </div>

              <div style={{ display: 'grid', gap: 6, marginTop: 16 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t('profile.slug')}
                </span>
                <div style={staticFieldStyle}>
                  {organisation?.slug ?? org?.slug ?? '—'}
                </div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                  {t('profile.defaultLocaleHint')}
                </div>
              </div>

              {workspaceFeedback && (
                <div style={{ marginTop: 14, fontSize: 13, color: toneColor(workspaceFeedback.tone) }}>
                  {workspaceFeedback.message}
                </div>
              )}

              {canManageWorkspace && (
                <div style={{ marginTop: 16 }}>
                  <Button onClick={handleSaveWorkspace} disabled={savingWorkspace || loading}>
                    {savingWorkspace ? t('profile.saving') : t('profile.save')}
                  </Button>
                </div>
              )}
            </Card>
            )}

            {activeSection === 'team' && (
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                    {t('members.title')}
                  </div>
                  <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                    {t('members.description')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Badge variant="info">{t('members.total', { count: workspaceMembersSummary.users })}</Badge>
                  <Badge variant="success">{t('members.active', { count: workspaceMembersSummary.active })}</Badge>
                  <Badge variant="neutral">{t('members.inactive', { count: workspaceMembersSummary.inactive })}</Badge>
                </div>
              </div>

              <div style={{ display: 'grid', gap: 12 }}>
                {members.map((member) => {
                  const isOwnerMember = member.role === 'owner';
                  const isSelf = member.userId === user?.id;
                  const canEditRole = isOwner && !isOwnerMember && !isSelf;
                  const canToggleStatus = canManageWorkspace && !isOwnerMember && member.userId !== user?.id && (isOwner || member.role === 'member');
                  const roleLockedReason = isOwnerMember
                    ? t('members.roleOwnerLocked')
                    : isSelf
                      ? t('members.roleSelfLocked')
                      : !canManageWorkspace
                        ? t('members.roleReadOnly')
                        : !isOwner
                          ? member.role === 'admin'
                            ? t('members.roleAdminLocked')
                            : t('members.roleAdminViewerOnly')
                          : t('members.roleManaged');
                  const statusLockedReason = isOwnerMember
                    ? t('members.statusOwnerLocked')
                    : isSelf
                      ? t('members.statusSelfLocked')
                      : !canManageWorkspace
                        ? t('members.roleReadOnly')
                        : !isOwner && member.role === 'admin'
                          ? t('members.statusAdminLocked')
                          : t('members.roleManaged');
                  return (
                    <div
                      key={member.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                        gap: 16,
                        alignItems: 'center',
                        border: `1px solid ${C.border}`,
                        borderRadius: 14,
                        background: C.card,
                        padding: '16px 18px',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>
                            {member.userFullName}
                          </span>
                          <Badge variant={roleBadgeVariant(member.role)}>
                            {roleLabel(member.role)}
                          </Badge>
                          <Badge variant={statusBadgeVariant(member.status)}>
                            {member.status === 'active' ? t('common:memberStatuses.active') : t('common:memberStatuses.inactive')}
                          </Badge>
                        </div>
                        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                          {member.userEmail}
                          {' · '}
                          {member.userLastLoginAt
                            ? t('members.lastSignIn', { value: formatDateTime(member.userLastLoginAt) })
                            : t('members.lastSignInMissing')}
                        </div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
                          {roleLockedReason}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gap: 10, justifyItems: 'end', minWidth: 220 }}>
                        <label style={{ display: 'grid', gap: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {t('members.roleLabel')}
                          </span>
                          {canEditRole ? (
                            <select
                              value={member.role}
                              disabled={updatingMemberId === member.id}
                              onChange={(event) => handleMemberRoleChange(member, event.target.value as 'admin' | 'member')}
                              style={{
                                minWidth: 150,
                                ...fieldStyle(),
                              }}
                            >
                                <option value="member">{t('common:roles.member')}</option>
                                <option value="admin">{t('common:roles.admin')}</option>
                              </select>
                          ) : (
                            <div
                              style={{
                                minWidth: 150,
                                minHeight: 40,
                                borderRadius: 10,
                                border: `1px solid ${C.border}`,
                                padding: '8px 12px',
                                fontSize: 14,
                                background: C.surfaceSecondary,
                                color: C.text,
                                display: 'flex',
                                alignItems: 'center',
                                fontWeight: 600,
                              }}
                            >
                                {roleLabel(member.role)}
                              </div>
                            )}
                          <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                            {roleLockedReason}
                          </span>
                        </label>

                        <div style={{ display: 'grid', gap: 6, justifyItems: 'end' }}>
                          <Button
                            variant="secondary"
                            disabled={!canToggleStatus || updatingMemberId === member.id}
                            onClick={() => handleMemberStatusChange(member, member.status === 'active' ? 'inactive' : 'active')}
                          >
                            {updatingMemberId === member.id
                              ? t('members.saving')
                              : member.status === 'active'
                                ? t('members.deactivate')
                                : t('members.reactivate')}
                          </Button>
                          <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, textAlign: 'right' }}>
                            {statusLockedReason}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {memberFeedback && (
                <div style={{ marginTop: 14, fontSize: 13, color: 'var(--acc-color-success)' }}>
                  {memberFeedback}
                </div>
              )}
              {memberError && (
                <div style={{ marginTop: 14, fontSize: 13, color: 'var(--acc-color-danger)' }}>
                  {memberError}
                </div>
              )}
            </Card>
            )}

            {activeSection === 'invites' && canManageWorkspace && (
              <Card>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                  {t('invites.title')}
                </div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                  {t('invites.description')}
                </div>
                {teamSeatLimitReached && (
                  <AlertBanner variant="danger" style={{ marginBottom: 16 }}>
                    {t('invites.limitReached', { count: teamSeatLimit ?? 0 })}
                  </AlertBanner>
                )}

                <div style={{ display: 'grid', gap: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                    <label style={{ display: 'grid', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('invites.email')}
                      </span>
                      <input
                        type="email"
                        value={inviteEmail}
                        disabled={inviting || loading || teamSeatLimitReached}
                        onChange={(event) => {
                          setInviteEmail(event.target.value);
                          setInviteFeedback(null);
                          setInviteError(null);
                        }}
                        placeholder="teammate@example.com"
                          style={fieldStyle()}
                      />
                    </label>

                    <label style={{ display: 'grid', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('invites.role')}
                      </span>
                      <select
                        value={inviteRole}
                        disabled={inviting || loading || teamSeatLimitReached}
                        onChange={(event) => setInviteRole(event.target.value as 'admin' | 'member')}
                        style={fieldStyle()}
                      >
                        <option value="member">{t('common:roles.member')}</option>
                        <option value="admin">{t('common:roles.admin')}</option>
                      </select>
                    </label>
                  </div>

                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('invites.message')}
                    </span>
                    <textarea
                      value={inviteMessage}
                      disabled={inviting || loading || teamSeatLimitReached}
                      onChange={(event) => {
                        setInviteMessage(event.target.value);
                        setInviteFeedback(null);
                        setInviteError(null);
                      }}
                      placeholder={t('invites.messagePlaceholder')}
                      style={{
                        width: '100%',
                        minHeight: 88,
                        borderRadius: 10,
                        border: `1px solid ${C.border}`,
                        padding: '10px 12px',
                        fontSize: 14,
                        resize: 'vertical',
                        fontFamily: 'inherit',
                      }}
                    />
                  </label>
                </div>

                {latestInviteLink && (
                  <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 12, border: '1px solid var(--acc-badge-info-border)', background: 'var(--acc-badge-info-bg)' }}>
                    <div style={{ fontWeight: 700, color: C.text, marginBottom: 6 }}>{t('invites.latestLinkTitle')}</div>
                    <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, wordBreak: 'break-all', marginBottom: 12 }}>
                      {latestInviteLink.link}
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => handleCopyInviteLink(latestInviteLink.inviteId, latestInviteLink.link)}
                      disabled={copyingInviteId === latestInviteLink.inviteId}
                    >
                      {copyingInviteId === latestInviteLink.inviteId ? t('invites.copying') : t('invites.copySecureLink')}
                    </Button>
                  </div>
                )}

                {inviteFeedback && (
                  <div style={{ marginTop: 14, fontSize: 13, color: 'var(--acc-color-success)', lineHeight: 1.6 }}>
                    {inviteFeedback}
                  </div>
                )}
                {inviteError && (
                  <div style={{ marginTop: 14, fontSize: 13, color: 'var(--acc-color-danger)', lineHeight: 1.6 }}>
                    {inviteError}
                  </div>
                )}

                <div style={{ marginTop: 16 }}>
                  <Button
                    onClick={handleInvite}
                    disabled={inviting || !inviteEmail.trim() || teamSeatLimitReached}
                  >
                    {inviting ? t('invites.creating') : t('invites.create')}
                  </Button>
                </div>
              </Card>
            )}

            {activeSection === 'notifications' && (
              <Card>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                  {t('profile.notificationEmail')}
                </div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                  Keep communication defaults and operator-facing alerts easy to update from one place.
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('profile.notificationEmail')}
                    </span>
                    <input
                      type="email"
                      value={notificationEmail}
                      disabled={!canManageWorkspace || loading}
                      onChange={(event) => {
                        setNotificationEmail(event.target.value);
                        setWorkspaceFeedback(null);
                      }}
                      placeholder={user?.email ?? t('profile.placeholderEmail')}
                      style={fieldStyle()}
                    />
                  </label>

                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('profile.defaultLocale')}
                    </span>
                    <select
                      value={defaultLocale}
                      disabled={!canManageWorkspace || loading}
                      onChange={(event) => {
                        setDefaultLocale(event.target.value as SupportedLocale);
                        setWorkspaceFeedback(null);
                      }}
                      style={fieldStyle()}
                    >
                      <option value="en">{t('common:locales.en')}</option>
                      <option value="de">{t('common:locales.de')}</option>
                    </select>
                  </label>
                </div>

                {workspaceFeedback && (
                  <div style={{ marginTop: 14, fontSize: 13, color: toneColor(workspaceFeedback.tone) }}>
                    {workspaceFeedback.message}
                  </div>
                )}

                {canManageWorkspace && (
                  <div style={{ marginTop: 16 }}>
                    <Button onClick={handleSaveWorkspace} disabled={savingWorkspace || loading}>
                      {savingWorkspace ? t('profile.saving') : t('profile.save')}
                    </Button>
                  </div>
                )}
              </Card>
            )}

            {activeSection === 'governance' && (
              <Card>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                  {t('posture.operationalSettings')}
                </div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                  Review evidence-retention posture and operational defaults without digging through team or invite controls.
                </div>
                <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: C.muted }}>{t('posture.maxPagesPerScan')}</span>
                    <strong style={{ color: C.text }}>{usageData?.limits.maxPagesPerScan ?? organisation?.settings.maxPagesPerScan ?? '—'}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: C.muted }}>{t('posture.scheduledScans')}</span>
                    <strong style={{ color: C.text }}>{usageData?.limits.scheduledScansEnabled ? t('posture.scheduledEnabled') : t('posture.scheduledLocked')}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: C.muted }}>{t('posture.evidenceRetention')}</span>
                    <strong style={{ color: C.text }}>{formatRetentionDays(organisation?.settings.evidenceRetentionDays)}</strong>
                  </div>
                  {retentionSummary && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: C.muted }}>{t('posture.cleanupCandidates')}</span>
                      <strong style={{ color: C.text }}>{retentionSummary.eligibleForCleanupCount + retentionSummary.authDiagnosticCleanupCandidates}</strong>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {activeSection === 'usage' && commercialSummary && usageData && (
              <Card>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                  {t('commercial.title')}
                </div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                  {t('commercial.description')}
                </div>
                <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: C.muted }}>{t('commercial.currentPlan')}</span>
                    <strong style={{ color: C.text }}>{commercialSummary.currentPlanLabel}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: C.muted }}>{t('commercial.sitesUsed')}</span>
                    <strong style={{ color: C.text }}>{usageData.usage.sitesCount} / {usageData.limits.maxSites}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: C.muted }}>{t('commercial.teamSeatsUsed')}</span>
                    <strong style={{ color: C.text }}>{usageData.usage.teamMembersCount} / {usageData.limits.maxTeamMembers}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: C.muted }}>{t('commercial.reportExport')}</span>
                    <strong style={{ color: C.text }}>{usageData.limits.complianceExportEnabled ? t('commercial.enabled') : t('commercial.locked')}</strong>
                  </div>
                </div>
              </Card>
            )}
          </div>

          <div style={{ display: 'grid', gap: 20 }}>
            {activeSection === 'overview' && (
            <Card>
              <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                {t('posture.title')}
              </div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                {t('posture.description')}
              </div>

              <div style={{ display: 'grid', gap: 14 }}>
                {commercialSummary && usageData && (
                  <div style={panelStyle}>
                    <div style={{ fontWeight: 700, color: C.text, marginBottom: 6 }}>{t('commercial.title')}</div>
                    <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 12 }}>
                      {t('commercial.description')}
                    </div>

                    <div style={{ display: 'grid', gap: 8, fontSize: 13, marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ color: C.muted }}>{t('commercial.currentPlan')}</span>
                        <strong style={{ color: C.text }}>{commercialSummary.currentPlanLabel}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ color: C.muted }}>{t('commercial.sitesUsed')}</span>
                        <strong style={{ color: C.text }}>{usageData.usage.sitesCount} / {usageData.limits.maxSites}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ color: C.muted }}>{t('commercial.monthlyScans')}</span>
                        <strong style={{ color: C.text }}>{usageData.usage.scansThisMonth} / {usageData.limits.maxScansPerMonth}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ color: C.muted }}>{t('commercial.teamSeatsUsed')}</span>
                        <strong style={{ color: C.text }}>{usageData.usage.teamMembersCount} / {usageData.limits.maxTeamMembers}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ color: C.muted }}>{t('commercial.reportExport')}</span>
                        <strong style={{ color: C.text }}>
                          {usageData.limits.complianceExportEnabled ? t('commercial.enabled') : t('commercial.locked')}
                        </strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ color: C.muted }}>{t('commercial.authenticatedScanning')}</span>
                        <strong style={{ color: C.text }}>
                          {usageData.limits.authenticatedScanningEnabled ? t('commercial.enabled') : t('commercial.locked')}
                        </strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ color: C.muted }}>{t('commercial.alerts')}</span>
                        <strong style={{ color: C.text }}>
                          {usageData.limits.alertsEnabled ? t('commercial.enabled') : t('commercial.locked')}
                        </strong>
                      </div>
                    </div>

                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 12 }}>
                      {t(`commercial.${commercialSummary.pressureKey}`)}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>
                      {t(`commercial.${commercialSummary.postureKey}`)}
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Link
                        to="/billing"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 38,
                          padding: '8px 14px',
                          borderRadius: 10,
                          border: `1px solid ${C.border}`,
                          background: C.buttonSecondary,
                          color: C.text,
                          textDecoration: 'none',
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {t('commercial.openBilling')}
                      </Link>
                      {!usageData.limits.complianceExportEnabled && (
                        <Link
                          to="/billing?plan=growth&source=workspace"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 38,
                            padding: '8px 14px',
                            borderRadius: 10,
                            border: '1px solid transparent',
                            background: 'var(--acc-color-primary-strong)',
                            color: '#fff',
                            textDecoration: 'none',
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {t('commercial.upgradeGrowth')}
                        </Link>
                      )}
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 14px', background: C.card }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      {t('posture.teamSeats')}
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>
                      {usageData?.usage.teamMembersCount ?? members.length} / {usageData?.limits.maxTeamMembers ?? '—'}
                    </div>
                  </div>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 14px', background: C.card }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      {t('posture.reportPacks')}
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>
                      {usageData?.limits.complianceExportEnabled ? t('posture.reportPacksEnabled') : t('posture.reportPacksLocked')}
                    </div>
                  </div>
                </div>

                <div style={panelStyle}>
                  <div style={{ fontWeight: 700, color: C.text, marginBottom: 10 }}>{t('posture.operationalSettings')}</div>
                  <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: C.muted }}>{t('posture.maxPagesPerScan')}</span>
                      <strong style={{ color: C.text }}>{usageData?.limits.maxPagesPerScan ?? organisation?.settings.maxPagesPerScan ?? '—'}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: C.muted }}>{t('posture.scheduledScans')}</span>
                      <strong style={{ color: C.text }}>{usageData?.limits.scheduledScansEnabled ? t('posture.scheduledEnabled') : t('posture.scheduledLocked')}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: C.muted }}>{t('posture.evidenceRetention')}</span>
                      <strong style={{ color: C.text }}>{formatRetentionDays(organisation?.settings.evidenceRetentionDays)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: C.muted }}>{t('posture.pendingInvites')}</span>
                      <strong style={{ color: C.text }}>{pendingInvites.length}</strong>
                    </div>
                  </div>
                </div>

                {retentionSummary && (
                  <div style={panelStyle}>
                    <div style={{ fontWeight: 700, color: C.text, marginBottom: 10 }}>{t('posture.retentionSummaryTitle')}</div>
                    <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ color: C.muted }}>{t('posture.activeArtifacts')}</span>
                        <strong style={{ color: C.text }}>{retentionSummary.activeCount}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ color: C.muted }}>{t('posture.archivedArtifacts')}</span>
                        <strong style={{ color: C.text }}>{retentionSummary.archivedCount}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ color: C.muted }}>{t('posture.cleanupCandidates')}</span>
                        <strong style={{ color: C.text }}>{retentionSummary.eligibleForCleanupCount + retentionSummary.authDiagnosticCleanupCandidates}</strong>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                      {t('posture.retentionHint')}
                    </div>
                  </div>
                )}
              </div>
            </Card>
            )}

            {activeSection === 'invites' && canManageWorkspace && (
              <Card>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                  {t('invites.pendingRecentTitle')}
                </div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                  {t('invites.pendingRecentDescription')}
                </div>

                <div style={{ display: 'grid', gap: 10 }}>
                  {invites.length === 0 ? (
                    <div style={{ border: `1px dashed ${C.border}`, borderRadius: 12, padding: '18px 16px', color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
                      {t('invites.empty')}
                    </div>
                  ) : invites.map((invite) => (
                    <div
                      key={invite.id}
                      style={{
                        border: `1px solid ${C.border}`,
                        borderRadius: 12,
                        padding: '14px 16px',
                        background: C.card,
                        display: 'grid',
                        gap: 10,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ fontWeight: 700, color: C.text }}>{invite.email}</span>
                            <Badge variant={inviteStatusVariant(invite.status)}>{inviteStatusLabel(invite.status)}</Badge>
                            <Badge variant={roleBadgeVariant(invite.role)}>{roleLabel(invite.role)}</Badge>
                          </div>
                          <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                            {t('invites.invitedAt', {
                              created: formatDateTime(invite.createdAt),
                              expires: formatDateTime(invite.expiresAt),
                            })}
                          </div>
                          {invite.message && (
                            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginTop: 6 }}>
                              {t('invites.note', { message: invite.message })}
                            </div>
                          )}
                        </div>
                        {invite.status === 'pending' && (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {latestInviteLink?.inviteId === invite.id && (
                              <Button
                                variant="secondary"
                                onClick={() => handleCopyInviteLink(invite.id, latestInviteLink.link)}
                                disabled={copyingInviteId === invite.id}
                              >
                                {copyingInviteId === invite.id ? t('invites.copying') : t('invites.copyInviteLink')}
                              </Button>
                            )}
                            {latestInviteLink?.inviteId !== invite.id && (
                              <Button
                                variant="secondary"
                                onClick={() => handleCopyInviteLink(invite.id, '')}
                                disabled={copyingInviteId === invite.id}
                              >
                                {copyingInviteId === invite.id ? t('invites.copying') : t('invites.copyInviteLink')}
                              </Button>
                            )}
                            <Button
                              variant="secondary"
                              onClick={() => handleRevokeInvite(invite.id)}
                              disabled={revokingInviteId === invite.id}
                            >
                              {revokingInviteId === invite.id ? t('invites.revoking') : t('invites.revoke')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {activeSection === 'notifications' && preferences && preferenceDraft && (
              <Card>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                  {t('preferences.title')}
                </div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                  {t('preferences.description')}
                </div>

                <div style={{ display: 'grid', gap: 10 }}>
                  {(Object.keys(preferenceDraft) as Array<keyof AlertPreferenceMap>).map((key) => (
                    <label
                      key={key}
                      style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'center',
                        border: `1px solid ${C.border}`,
                        borderRadius: 10,
                        padding: '12px 14px',
                        background: C.card,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={preferenceDraft[key]}
                        disabled={!canManageWorkspace}
                        onChange={(event) => handlePreferenceChange(key, event.target.checked)}
                      />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontWeight: 600, color: C.text, fontSize: 13 }}>
                          {preferenceLabel(key)}
                        </span>
                        <span style={{ display: 'block', color: C.muted, fontSize: 12, marginTop: 3 }}>
                          {preferenceDraft[key] ? t('preferences.visible') : t('preferences.muted')}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>

                {preferenceFeedback && (
                  <div style={{ marginTop: 14, fontSize: 13, color: toneColor(preferenceFeedback.tone) }}>
                    {preferenceFeedback.message}
                  </div>
                )}

                {canManageWorkspace && (
                  <div style={{ marginTop: 16 }}>
                    <Button onClick={handleSavePreferences} disabled={savingPreference}>
                      {savingPreference ? t('preferences.saving') : t('preferences.save')}
                    </Button>
                  </div>
                )}
              </Card>
            )}

            {activeSection === 'governance' && canManageWorkspace && (
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 6 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                    {t('githubIntegration.title')}
                  </div>
                  <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                    {t('githubIntegration.description')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Badge variant={githubEnabled ? 'success' : 'neutral'}>
                    {githubEnabled ? t('githubIntegration.enabledStatus') : t('githubIntegration.disabledStatus')}
                  </Badge>
                  <Badge variant={githubTokenConfigured ? 'info' : 'warning'}>
                    {githubTokenConfigured ? t('githubIntegration.tokenConfigured') : t('githubIntegration.tokenMissing')}
                  </Badge>
                  {githubLastTestStatus && (
                    <Badge variant={githubLastTestStatus === 'passed' ? 'success' : 'danger'}>
                      {githubLastTestStatus === 'passed'
                        ? t('githubIntegration.lastTestPassed')
                        : t('githubIntegration.lastTestFailed')}
                    </Badge>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gap: 14, marginTop: 16 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('githubIntegration.repoOwner')}
                  </span>
                  <input
                    type="text"
                    value={githubRepoOwner}
                    disabled={!canManageWorkspace || loading || savingGitHubIntegration}
                    onChange={(event) => {
                      setGitHubRepoOwner(event.target.value);
                      setGitHubIntegrationFeedback(null);
                    }}
                    placeholder="ablecomply"
                    style={fieldStyle()}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('githubIntegration.repoName')}
                  </span>
                  <input
                    type="text"
                    value={githubRepoName}
                    disabled={!canManageWorkspace || loading || savingGitHubIntegration}
                    onChange={(event) => {
                      setGitHubRepoName(event.target.value);
                      setGitHubIntegrationFeedback(null);
                    }}
                    placeholder="frontend-remediation"
                    style={fieldStyle()}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('githubIntegration.accessToken')}
                  </span>
                  <input
                    type="password"
                    value={githubAccessToken}
                    disabled={!canManageWorkspace || loading || savingGitHubIntegration}
                    onChange={(event) => {
                      setGitHubAccessToken(event.target.value);
                      setGitHubIntegrationFeedback(null);
                    }}
                    placeholder={githubTokenConfigured ? '••••••••••' : 'github_pat_...'}
                    autoComplete="new-password"
                    style={fieldStyle()}
                  />
                  <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                    {githubTokenConfigured
                      ? t('githubIntegration.tokenStoredHint')
                      : t('githubIntegration.tokenRequiredHint')}
                  </span>
                </label>

                <label
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    padding: '12px 14px',
                    background: C.card,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={githubEnabled}
                    disabled={!canManageWorkspace || loading || savingGitHubIntegration}
                    onChange={(event) => {
                      setGitHubEnabled(event.target.checked);
                      setGitHubIntegrationFeedback(null);
                    }}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    <span style={{ display: 'block', fontWeight: 600, color: C.text, fontSize: 13 }}>
                      {t('githubIntegration.enabled')}
                    </span>
                    <span style={{ display: 'block', color: C.muted, fontSize: 12, marginTop: 3, lineHeight: 1.6 }}>
                      {t('githubIntegration.enabledHint')}
                    </span>
                  </span>
                </label>

                <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 14px', background: C.card }}>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                    {githubLastTestedAt
                      ? t('githubIntegration.lastTested', { value: formatDateTime(githubLastTestedAt) })
                      : t('githubIntegration.lastTestPending')}
                  </div>
                  {githubLastTestError && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--acc-badge-warning-text)', lineHeight: 1.6 }}>
                      {t('githubIntegration.lastTestError', { message: githubLastTestError })}
                    </div>
                  )}
                </div>
              </div>

              {githubIntegrationFeedback && (
                <div style={{ marginTop: 14, fontSize: 13, color: toneColor(githubIntegrationFeedback.tone) }}>
                  {githubIntegrationFeedback.message}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
                <Button
                  onClick={handleSaveGitHubIntegration}
                  disabled={!canSaveGitHubIntegration || savingGitHubIntegration || loading}
                >
                  {savingGitHubIntegration ? t('githubIntegration.saving') : t('githubIntegration.save')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleTestGitHubIntegration}
                  disabled={!canTestGitHubIntegration || testingGitHubIntegration || loading}
                >
                  {testingGitHubIntegration ? t('githubIntegration.testing') : t('githubIntegration.test')}
                </Button>
              </div>
            </Card>
            )}

            {activeSection === 'governance' && (
            <Card>
              <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                {t('guidance.title')}
              </div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.7 }}>
                {t('guidance.body')}
              </div>
            </Card>
            )}
          </div>
        </div>
    </Container>
  );
}
